/* iter-p2 — Coach Suggestions History drawer.
 *
 * Small dialog that lists the caller's recent Live-Coach suggestions
 * (last 50) so a writer can review "what did Claude suggest during
 * this scene?" — useful for post-mortems and for spotting patterns
 * ("Claude keeps flagging Ava's voice — I should tighten her Bible").
 *
 * Opens on demand from the LiveCoach popover.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  History, Loader2, Users, Milestone, Feather, BookOpen, Zap, Sparkles, Filter,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const KIND_ICON = {
  character: Users, beat: Milestone, voice: Feather, bible: BookOpen,
  pacing: Zap, empty: Sparkles,
};

export default function CoachHistoryDialog({ open, onOpenChange, projectId, onOpenTool }) {
  const [rows, setRows] = useState([]);
  const [loading, setL] = useState(false);
  // iter-p2-c — filter state. `kind` filters server-side; `chapterId`
  // filters client-side (values are known only in the UI).
  const [kind, setKind] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');

  const load = useCallback(async () => {
    if (!open) return;
    setL(true);
    try {
      const params = {};
      if (projectId) params.project_id = projectId;
      if (kind)      params.kind       = kind;
      const r = await axios.get(`${API}/coach/history`, { params });
      setRows(r.data?.suggestions || []);
    } catch { setRows([]); }
    finally { setL(false); }
  }, [open, projectId, kind]);
  useEffect(() => { load(); }, [load]);

  // Distinct chapter ids present in the current row set — feeds the
  // client-side chapter filter.
  const chapterOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.chapter_id) s.add(r.chapter_id);
    return Array.from(s);
  }, [rows]);

  const shown = useMemo(() => {
    if (!chapterFilter) return rows;
    return rows.filter((r) => r.chapter_id === chapterFilter);
  }, [rows, chapterFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[80vh] max-w-2xl overflow-hidden bg-white p-0"
        data-testid="coach-history-dialog"
      >
        <DialogHeader className="border-b bg-gradient-to-r from-oxblood-50 to-amber-50 px-4 py-3">
          <DialogTitle className="flex items-center gap-2 text-[15px]"
                       style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <History className="h-4 w-4 text-oxblood-700" />
            Coach suggestions history
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto p-3" data-testid="coach-history-list">
          {/* iter-p2-c — filter row */}
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-panel-border bg-secondary/30 p-2 text-[10.5px] text-muted-foreground"
               data-testid="coach-history-filters">
            <Filter className="h-3 w-3" />
            <label className="flex items-center gap-1">
              Kind
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="rounded-sm border border-panel-border bg-background px-1 py-0.5 text-[10.5px]"
                data-testid="coach-history-kind"
              >
                <option value="">all</option>
                <option value="character">character</option>
                <option value="beat">beat</option>
                <option value="voice">voice</option>
                <option value="bible">bible</option>
                <option value="pacing">pacing</option>
              </select>
            </label>
            <label className="flex items-center gap-1">
              Chapter
              <select
                value={chapterFilter}
                onChange={(e) => setChapterFilter(e.target.value)}
                className="rounded-sm border border-panel-border bg-background px-1 py-0.5 text-[10.5px]"
                data-testid="coach-history-chapter"
              >
                <option value="">all</option>
                {chapterOptions.map((c) => (
                  <option key={c} value={c}>{c.slice(0, 12)}…</option>
                ))}
              </select>
            </label>
            <span className="ml-auto text-[10.5px]">
              {shown.length} of {rows.length}
            </span>
          </div>

          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          )}
          {!loading && shown.length === 0 && (
            <p className="p-4 text-center text-[11.5px] text-muted-foreground"
               data-testid="coach-history-empty">
              {rows.length === 0
                ? 'No coach suggestions yet. Keep drafting — Claude will nudge you every ~500 words.'
                : 'No suggestions match your current filters.'}
            </p>
          )}
          {!loading && shown.map((r) => {
            const Icon = KIND_ICON[r.kind] || Sparkles;
            return (
              <div key={r.id}
                   className="mb-2 rounded-md border border-panel-border bg-card p-2.5"
                   data-testid={`coach-history-row-${r.id}`}>
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-primary">
                  <Icon className="h-3 w-3" />
                  {r.kind}
                  <span className="ml-auto text-neutral-500">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-1 text-[12.5px] font-semibold text-foreground">
                  {r.title}
                </div>
                {r.tip && (
                  <p className="mt-0.5 text-[11.5px] leading-relaxed text-foreground/85">
                    {r.tip}
                  </p>
                )}
                {r.tool_hint && (
                  <button
                    type="button"
                    onClick={() => onOpenTool?.(r.tool_hint)}
                    className="mt-1 inline-flex items-center gap-1 rounded-sm bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary hover:bg-primary/20"
                    data-testid={`coach-history-tool-${r.id}`}
                  >
                    Open {r.tool_hint.replace(/-/g, ' ')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
